import { last } from 'lib/array/arrayUtils'
import type { TransportConnection } from 'tripPlanner/api/types'
import type { TripPlannerCommittedStateEntry } from 'tripPlanner/assistant/surfaceSnapshots/committedStateEntry'
import { boundActivityDisplayText } from 'tripPlanner/assistant/surfaceSnapshots/surfaceActivityDisplayText'
import { buildCommittedStateExpiresAt } from 'tripPlanner/assistant/surfaceSnapshots/expiry'

// Shape-agnostic: both App.JourneyV2 (flightGroup.flights) and App.Checkout.FlightItem (flights)
// reduce to the same 3 fields for display purposes -- shared here so the two shape-specific
// extractions below can't drift on wording from each other.
function formatFlightLegRouteText(originAirportCode: string, destinationAirportCode: string, departureDate: string): string {
  return `${originAirportCode} to ${destinationAirportCode}, departing ${departureDate}`
}

// First flight's departure, last flight's arrival -- matches generateFlightCheckoutItem's own
// derivation (lib/checkout/flights/cart.ts) for the exact same JourneyV2 -> route-text problem,
// including its use of `last()` over manual index arithmetic for the connecting-flight case.
function formatJourneyRouteText(journey: App.JourneyV2): string | undefined {
  const flights = journey.flightGroup.flights
  const firstFlight = flights[0]
  const lastFlight = last(flights)
  if (!firstFlight || !lastFlight) return undefined
  return formatFlightLegRouteText(firstFlight.departureAirport, lastFlight.arrivalAirport, firstFlight.departingDate)
}

// App.Checkout.FlightItem's own `flights` array is fixed-position, not a variable-length segment
// merge: `flights[0]` is always the outbound flight, and `flights[1]` is the return flight only
// when fareType is 'return' -- mirrors `isIncompleteReturnFlightMultiCartItem`'s own indexing
// convention (multiCartUtils.ts), the established precedent for reading this exact shape.
function formatFlightItemRouteText(flightItem: App.Checkout.FlightItem): string | undefined {
  const outbound = flightItem.flights[0]
  if (!outbound) return undefined
  const outboundText = formatFlightLegRouteText(outbound.departingAirportCode, outbound.arrivalAirportCode, outbound.departingDate)

  const returnFlight = flightItem.fareType === 'return' ? flightItem.flights[1] : undefined
  if (!returnFlight) return `Selected flight ${outboundText}`
  const returnText = formatFlightLegRouteText(returnFlight.departingAirportCode, returnFlight.arrivalAirportCode, returnFlight.departingDate)
  return `Selected flight ${outboundText}, returning ${returnText}`
}

// Same-journey comparison for the availability-driven refresh path, over App.Checkout.FlightItem's
// own shape -- simpler than hasSelectedJourneyChanged (useTripChatFlightCartActions.tsx) since both
// sides here are already the same type, with no cross-shape reconciliation needed. Compares
// journeyKey at each fixed position (see formatFlightItemRouteText's own comment on why these are
// fixed, not variable-length).
export function hasFlightItemJourneyChanged(previous: App.Checkout.FlightItem, next: App.Checkout.FlightItem): boolean {
  const outboundChanged = previous.flights[0]?.journeyKey !== next.flights[0]?.journeyKey
  const returnChanged = previous.fareType === 'return' || next.fareType === 'return'
    ? previous.flights[1]?.journeyKey !== next.flights[1]?.journeyKey
    : false
  return outboundChanged || returnChanged
}

// Keyed per intra-trip leg (decided directly -- a multi-stop trip can have a distinct flight
// selection per leg, e.g. stop A->B and B->C are different facts), matching
// `TripPlanCanvas.tsx`'s own `${departureStopId}-${arrivalStopId}` composite identity for a
// transport connection. Returns undefined (skip dispatch, not a placeholder entry) when the
// journey has no resolvable route -- shouldn't happen for a successfully persisted selection, but
// there's no meaningful committed-state fact to publish without a route to describe.
export function buildSelectedFlightItineraryCommittedStateEntry(
  transport: TransportConnection,
  departingJourney: App.JourneyV2,
  returningJourney: App.JourneyV2 | undefined,
): TripPlannerCommittedStateEntry | undefined {
  const outboundText = formatJourneyRouteText(departingJourney)
  if (!outboundText) return undefined

  const returnText = returningJourney ? formatJourneyRouteText(returningJourney) : undefined
  const displayText = returnText
    ? `Selected flight ${outboundText}, returning ${returnText}`
    : `Selected flight ${outboundText}`

  return {
    key: `flight:leg:${transport.departureStopId}-${transport.arrivalStopId}`,
    kind: 'selected_flight_itinerary',
    label: 'Selected flight itinerary',
    displayText: boundActivityDisplayText(displayText),
    sourcePath: 'trip_planner.cart.flight_selection',
    dataClassification: 'personalised_surface',
    expiresAt: buildCommittedStateExpiresAt(),
  }
}

// Same fact as buildSelectedFlightItineraryCommittedStateEntry, sourced from
// App.Checkout.FlightItem instead of App.JourneyV2 -- used by the availability-driven refresh path
// (handleActiveFlightRefresh), which only ever has the checkout-mapped shape available, not a
// JourneyV2. Returns undefined (skip dispatch) when the item has no resolvable route, matching the
// JourneyV2 builder's own guard.
export function buildRefreshedFlightItineraryCommittedStateEntry(
  transport: TransportConnection,
  flightItem: App.Checkout.FlightItem,
): TripPlannerCommittedStateEntry | undefined {
  const displayText = formatFlightItemRouteText(flightItem)
  if (!displayText) return undefined

  return {
    key: `flight:leg:${transport.departureStopId}-${transport.arrivalStopId}`,
    kind: 'selected_flight_itinerary',
    label: 'Selected flight itinerary',
    displayText: boundActivityDisplayText(displayText),
    sourcePath: 'trip_planner.cart.flight_selection',
    dataClassification: 'personalised_surface',
    expiresAt: buildCommittedStateExpiresAt(),
  }
}

// Dispatched on cart deletion so a removed selection stops appearing immediately, rather than
// sitting as a stale "selected" fact until its own 24h TTL lapses on its own. Already-expired
// (epoch zero) rather than a real "no longer selected" fact: `collectLiveCommittedStateEntries`
// prunes any non-live entry on its very next read, which is exactly the desired effect here --
// this key simply stops being reported, the same as if it had never been set at all.
export function buildClearedFlightItineraryCommittedStateEntry(transport: TransportConnection): TripPlannerCommittedStateEntry {
  return {
    key: `flight:leg:${transport.departureStopId}-${transport.arrivalStopId}`,
    kind: 'selected_flight_itinerary',
    label: 'Selected flight itinerary',
    displayText: 'No flight currently selected for this leg',
    sourcePath: 'trip_planner.cart.flight_selection',
    dataClassification: 'personalised_surface',
    expiresAt: new Date(0).toISOString(),
  }
}
